/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2019 ICEMEDX, Inc.
***********************************************/
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material';
import { Subscription } from 'rxjs';

import { IBaseView } from '../../../../icemedx-lib/src/components/baseview.component';
import {
  IcecontrolSettings,
  iceColumnDefinition,
  IcemattableSettings,
  IcepanelgroupSettings,
  IcepanelsectionSettings,
  IceexpansionpanelSettings,
  IcepanelcontainerRowSettings,
  IcepanelcontainerSettings,
  IcecontrolPermission,
  IcepanelSettings,
} from 'projects/icemedx-lib/src/components/models/icesettings';

import { IceEntity } from 'projects/icemedx-lib/src/models/core/IceEntity';
import { patient } from 'projects/icemedx-lib/src/models/domain/patient';
import { entity } from 'projects/icemedx-lib/src/models/common/entity';

import { AppService } from '../app.service';
import { IceNavService } from '../../../../icemedx-lib/src/components/ice-nav/ice-nav.service';
import { LoginService } from '../../../../icemedx-lib/src/login/login.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { Iceutility } from 'projects/icemedx-lib/src/services/iceutility';
import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';
import { MessageService } from 'projects/icemedx-lib/src/services/message/message.service';
import { DomainProfile } from '../../../../domainprofile/src/app/domainprofile';
import { VendorService } from '../../../../icemedx-lib/src/services/vendor/vendor.service';
import { ProviderService } from '../../../../icemedx-lib/src/services/provider/provider.service';
import { PharmacyService } from '../../../../icemedx-lib/src/services/pharmacy/pharmacy.service';
import { PayerService } from '../../../../icemedx-lib/src/services/payer/payer.service';
import { MedicalcodesService } from '../../../../icemedx-lib/src/services/medicalcodes/medicalcodes.service';
import { basecode } from '../../../../icemedx-lib/src/models/ehr/subs/basecode';
import { PatientAppService } from '../../../../icemedx-lib/src/services/app-services/patient.app.service';
import { PatientService } from '../../../../icemedx-lib/src/services/patient/patient.service';

@Component({
  selector: 'app-phrcontact',
  templateUrl: './phrcontact.component.html',
  styleUrls: ['./phrcontact.component.scss'],
  providers: [AppService, IceNavService]
})
export class PhrcontactComponent extends DomainProfile implements OnInit {
  // Data Members
  patientObj: patient = null;
  private patientReadySub: Subscription = null;
  profileViewSettings: IcepanelcontainerSettings = null;
  btnSaveProfile: IcecontrolSettings = new IcecontrolSettings("SaveButton", { text: "Save Profile", class: [], glyphClass: "fa-plus-circle" }, null, null);

  constructor(appSvc: AppService,
    domainSvc: DomainService,
    vendorSvc: VendorService,
    proSvc: ProviderService,
    pharmSvc: PharmacyService,
    payerSvc: PayerService,
    medCodeSvc: MedicalcodesService,
    messageSvc: MessageService,
    loginSvc: LoginService,
    navSvc: IceNavService,
    router: Router,
    private ptSvc: PatientService) {
    super(appSvc, domainSvc, vendorSvc, proSvc, pharmSvc, payerSvc, medCodeSvc, messageSvc, loginSvc, navSvc, router);

    this.getAppService().defaultMenus();
    this.getAppService().addBreadcrumb("Patient Contact", {}, true, "");

    this.patientReadySub = this.getAppService().onPatientReady(this.getAppService().getCurrentAccountType() == IceTypes.ACCOUNT_TYPE.actPatient ? this.getAppService().getCurrentAccount() : this.getAppService().getCurrentPatient()).subscribe(
    pt => {
        this.profileViewSettings = this.viewSetup(pt);
    },
    error => {
    });
  }

  ngOnInit() {
    this.getAppService().refreshMenus();
  }

  ngOnDestroy() {
    this.patientReadySub.unsubscribe();
  }

  viewSetup(dat: IceEntity): IcepanelcontainerSettings {
    let setup: IcepanelcontainerSettings = null;
    try {
      let feaAccess = this.getAppService().getPermission(userTypes.USER_FEATURES.ufDemographics).default_access;
      this.btnSaveProfile.permissions = new IcecontrolPermission(userTypes.USER_FEATURES.ufDemographics, feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone ? userTypes.USER_FEATURE_ACCESS.ufaReadOnly : feaAccess);

      if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
        return null;
      }
      if (dat == null || dat["length"] == 0) {
        return null;
      }

      this.patientObj = <patient>dat;

      //////////////////////
      // Name
      if (this.patientObj.COUNTRIES == null || this.patientObj.COUNTRIES.length == 0) {
        this.patientObj.COUNTRIES = [new basecode("COUNTRY")];
      }
      if (this.patientObj.CURRENCIES == null || this.patientObj.CURRENCIES.length == 0) {
        this.patientObj.CURRENCIES = [new basecode("CURRENCY")];
      }

      let cntryCode: basecode = this.patientObj.COUNTRIES[0];
      let curcyCode: basecode = this.patientObj.CURRENCIES[0];
      let nameControls = super.viewName(this.patientObj.trans_account, this.patientObj.ENTS, this.patientObj.entity_key, false, "", cntryCode, curcyCode);
      let nameSections: IcepanelsectionSettings[] = [
        new IcepanelsectionSettings("NameViewSection", { class: ["yalla"], styles: { width: "100%" } }, null, nameControls, null)
      ];

      //////////////////////
      // Contact Info
      let contactTables = super.viewContact(this.patientObj.CONS);
      let contactSections = this.viewContactSection(contactTables, true);

      //////////////////////
      // Address Info
      let addressTables = this.viewAddress(this.patientObj.ADS);
      let addressSections = this.viewAddressSection(addressTables, true);

      //////////////////////
      // Identification Info
      let idCodeList = ["SY", "1W", "ZZ", "PID"];
      let idTables = super.viewIdentification(this.patientObj.IDS, idCodeList);
      let idSections = this.viewIdentificationSection(idTables, idCodeList, true);

      //////////////////////
      // Subscriptions
      let subscriptionTables = this.viewSubscription([]);
      let subscriptionSections = this.viewSubscriptionSection(subscriptionTables, this.patientObj.COUNTRIES, this.patientObj.CURRENCIES, true);

      let namefrm: IcepanelSettings = new IceexpansionpanelSettings("NameView", "", "Name", "", nameSections, null, null, null)
        .enableForm();
      let nameFrmGrp = namefrm.getForm();
      let panels: IcepanelSettings[] = [
        namefrm,
        new IceexpansionpanelSettings("ContactView", "", "Contact Information", "", contactSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("AddressView", "", "Address Information", "", addressSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("IdentificationView", "", "Identification Information", "", idSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("SubscriptionView", "", "Subscription(s) and Billing History", "", subscriptionSections, null, null, null)
          .enableForm(nameFrmGrp),
      ];

      let panelrows: IcepanelcontainerRowSettings[] = [new IcepanelcontainerRowSettings(panels)];
      setup = new IcepanelcontainerSettings("ContactViewContainer", panelrows);

    }
    catch (e) {
      console.log(e);
    }
    return setup;
  }

  //////////////////////////////////
  // Toolbar Events
  onToolbarMenuItemOpen(event) {
    this.getAppService().onDefaultToolbarMenuItemOpen(event);
  }
  onToolbarMenuItemClick(event) {
    this.getAppService().onDefaultToolbarMenuItemClick(event, this.getRouter());
  }

  // Events
  onControlChange(event) {
    super.onControlChange(event);
  }

  onButtonClick(event) {
    if (Iceutility.isdefined(event) && Iceutility.isdefined(event.isAllowedToContinue)) {
      if (!event.isAllowedToContinue && (event.button === "Next" || event.button === "Complete")) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "Please set default country and currency before continuing");
        window.scrollTo(0, 0);
      }
      return;
    }
    this.onDefaultButtonClick(event, [], [], this.editInboundRow);
  }

  public onRowClick(event) {
    super.onRowClick(event);
  }

  public onAddRowButtonClick(event) {
    this.onDefaultAddRowButtonClick(event,
      this.patientObj.CONS,
      this.patientObj.ADS,
      this.patientObj.IDS,
      [],
      [],
      [],
      []);
  }

  onSaveProfile(event) {
    if (false == this.updateName(this.patientObj.COUNTRIES, this.patientObj.CURRENCIES)) {
      this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "Error: Unable to save patient profile information.");
      return;
    }

    this.updateContact(this.patientObj.CONS);
    this.updateAddress(this.patientObj.ADS);
    this.updateIdentification(this.patientObj.IDS);
    this.ptSvc.updatePatient("", IceTypes.ACCOUNT_TYPE.actNone, this.patientObj, "").subscribe(
      data => {
        this.getRouter().navigate([this.getNavService().buildRoute("patientdashboard", false)]);
      },
      error => {
        console.log(error);
      });
  }
}
